import { useCallback, useEffect, useRef, useState } from 'react';
import type { LiveFeedConnection } from '../../../shared/components/ConnectionBadge';
import { parsePositionFeedEvent } from '../../../shared/policies/parse-position-feed-event';
import { LIVE_MAP_STREAM_URL } from '../../live-map/live-map.api';
import { applyLiveFixes } from '../policies/trip-track-live.policy';
import { getTripTrack, type TrackPoint } from '../trips.api';

export type TripTrackState = { status: 'loading' } | { status: 'error' } | { status: 'loaded'; points: TrackPoint[]; isThinned: boolean };

/** `EventSource.CLOSED`, spelled out because a test environment may have no `EventSource` to read it from. */
const CLOSED = 2;
/** A fix moves the deadline verdicts, but a truck reports every few seconds; the page re-reads them at most this often. */
const FRESH_FIX_REFRESH_MS = 30_000;

interface UseTripTrackArgs {
  tripId: string;
  truckId: string | null;
  /** False once the trip is over: there is nothing to follow, so no stream is opened. */
  watchLive: boolean;
  /** Called, at most every 30 seconds, when a fix for this trip's truck arrives. */
  onFreshFix: () => void;
}

/**
 * The trip's track (what the server holds between dispatch and close-out) kept moving by the position stream. The stream is opened
 * first and the track fetched on each `onopen`, so a fix between the two is not lost; fixes of other trucks are dropped. A refused
 * stream (the viewer lacks `fleet.view`, which the stream needs) is closed for good, and the badge says "Not live" while the track
 * is still shown as the server last had it.
 */
export function useTripTrack({ tripId, truckId, watchLive, onFreshFix }: UseTripTrackArgs) {
  const [state, setState] = useState<TripTrackState>({ status: 'loading' });
  const [connection, setConnection] = useState<LiveFeedConnection>(typeof EventSource === 'undefined' ? 'unavailable' : 'connecting');
  const lastRefreshAt = useRef(0);
  const onFreshFixRef = useRef(onFreshFix);

  useEffect(() => {
    onFreshFixRef.current = onFreshFix;
  });

  const load = useCallback(async () => {
    try {
      const track = await getTripTrack(tripId);
      setState({ status: 'loaded', points: track.points, isThinned: track.isThinned });
    } catch {
      setState((held) => (held.status === 'loaded' ? held : { status: 'error' }));
    }
  }, [tripId]);

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    void load();
  }, [load]);

  useEffect(() => {
    let disposed = false;
    const reload = () => {
      if (!disposed) void load();
    };
    reload();
    if (!watchLive || typeof EventSource === 'undefined') {
      return () => {
        disposed = true;
      };
    }
    const source = new EventSource(LIVE_MAP_STREAM_URL);
    source.addEventListener('positions', (event) => {
      const parsed = parsePositionFeedEvent((event as MessageEvent<string>).data);
      if (!parsed) return;
      setState((held) => {
        if (held.status !== 'loaded') return held;
        const points = applyLiveFixes(held.points, truckId, parsed.positions);
        return points === held.points ? held : { ...held, points };
      });
      const now = Date.now();
      if (parsed.positions.some((entry) => entry.truckId === truckId) && now - lastRefreshAt.current >= FRESH_FIX_REFRESH_MS) {
        lastRefreshAt.current = now;
        onFreshFixRef.current();
      }
    });
    source.onopen = () => {
      setConnection('live');
      reload();
    };
    // A refused stream (no permission, an old server) is closed for good; a dropped one is retried by the browser.
    source.onerror = () => setConnection(source.readyState === CLOSED ? 'unavailable' : 'reconnecting');
    return () => {
      disposed = true;
      source.close();
    };
  }, [load, truckId, watchLive]);

  return { state, connection: watchLive ? connection : null, retry };
}
